(() => {
  const THEME_CONFIG = window.MMPOrderDateConfig || {};

  function parseDateList(value) {
    return String(value || '')
      .split(/[\n,]+/)
      .map((date) => date.trim())
      .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date));
  }

  const DATE_CONFIG = {
    timeZone: 'America/Toronto',

    delivery: {
      leadDays: Math.max(0, Number(THEME_CONFIG.deliveryLeadDays ?? 1) || 0),
      maxDays: Math.max(1, Number(THEME_CONFIG.deliveryWindowDays ?? 30) || 30),
      limitToCurrentMonth: false,
      blackoutDates: parseDateList(THEME_CONFIG.deliveryBlackoutDates)
    },

    pickup: {
      leadDays: Math.max(0, Number(THEME_CONFIG.pickupLeadDays ?? 1) || 0),
      limitToCurrentMonth: THEME_CONFIG.pickupLimitCurrentMonth !== false,
      blackoutDates: parseDateList(THEME_CONFIG.pickupBlackoutDates)
    }
  };

  const ORDER_GROUP_PROPERTY = '_mmp_order_group';
  const MCGILL_ORDER_GROUP = 'mcgill-convocation';
  const LEGACY_MCGILL_PROPERTY_NAMES = new Set([
    'convocation date',
    'convocation time',
    'mcgill convocation pickup'
  ]);
  const BLACKOUT_DATES = {
    delivery: new Set(DATE_CONFIG.delivery.blackoutDates),
    pickup: new Set(DATE_CONFIG.pickup.blackoutDates)
  };
  const SLOTS = [
    ['09:30-12:00', '9:30 AM – 12:00 PM'],
    ['12:00-15:00', '12:00 PM – 3:00 PM'],
    ['15:00-18:00', '3:00 PM – 6:00 PM'],
    ['18:00-20:00', '6:00 PM – 8:00 PM']
  ];
  const fetchCart = window.fetch.bind(window);
  let cart = null, saving = false, timer, validationError = '';

  const normalizeGroup = (value) => String(value ?? '').trim().toLowerCase();
  const normalizePropertyName = (value) => String(value ?? '').trim().toLowerCase();

  function blackoutDatesFor(methodName) {
    return BLACKOUT_DATES[methodName] || new Set();
  }

  function itemIsMcGill(item) {
    const properties = item?.properties || {};
    return normalizeGroup(properties[ORDER_GROUP_PROPERTY]) === MCGILL_ORDER_GROUP ||
      Object.keys(properties).some((key) => LEGACY_MCGILL_PROPERTY_NAMES.has(normalizePropertyName(key)));
  }

  function todayParts() {
    return Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: DATE_CONFIG.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
        .formatToParts(new Date())
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );
  }

  function formatUtcDate(date) {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
  }

  function ymd(offset = 0) {
    const parts = todayParts();
    return formatUtcDate(new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day + offset)));
  }

  function currentMonthEnd() {
    const parts = todayParts();
    return formatUtcDate(new Date(Date.UTC(+parts.year, +parts.month, 0)));
  }

  function rulesFor(methodName) {
    const settings = DATE_CONFIG[methodName] || DATE_CONFIG.delivery;
    const maxDays = Number(settings.maxDays);
    const max = Number.isFinite(maxDays) && maxDays >= 0
      ? ymd(maxDays)
      : settings.limitToCurrentMonth
        ? currentMonthEnd()
        : '';
    return {
      min: ymd(Math.max(0, Number(settings.leadDays) || 0)),
      max
    };
  }

  function nextYmd(dateValue) {
    const [year, month, day] = String(dateValue).split('-').map(Number);
    return formatUtcDate(new Date(Date.UTC(year, month - 1, day + 1)));
  }

  function isDateShape(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
  }

  function dateAllowed(value, methodName) {
    if (!isDateShape(value)) return false;
    const { min, max } = rulesFor(methodName);
    if (value < min) return false;
    if (max && value > max) return false;
    if (blackoutDatesFor(methodName).has(value)) return false;
    return true;
  }

  function hasAvailableDate(methodName) {
    const { min, max } = rulesFor(methodName);
    if (max && min > max) return false;
    if (!max) return true;

    const blockedDates = blackoutDatesFor(methodName);
    let candidate = min;
    while (candidate <= max) {
      if (!blockedDates.has(candidate)) return true;
      candidate = nextYmd(candidate);
    }
    return false;
  }

  function dateIssue(value, methodName) {
    const label = methodName === 'pickup' ? 'pickup' : 'delivery';
    if (!hasAvailableDate(methodName)) {
      return methodName === 'delivery'
        ? `No ${label} dates are available within the next ${DATE_CONFIG.delivery.maxDays} days.`
        : `No ${label} dates are available for the rest of this month.`;
    }
    if (!value) return `Please select a ${label} date.`;
    if (!isDateShape(value)) return `Please select a valid ${label} date.`;

    const { min, max } = rulesFor(methodName);
    if (value < min) {
      const leadDays = Math.max(0, Number(DATE_CONFIG[methodName]?.leadDays) || 0);
      return leadDays === 1
        ? `Same-day ${label} is not available. Please choose tomorrow or a later date.`
        : `Please choose a ${label} date at least ${leadDays} days from today.`;
    }
    if (max && value > max) {
      const maxDays = Number(DATE_CONFIG[methodName]?.maxDays);
      return Number.isFinite(maxDays) && maxDays >= 0
        ? `Please choose a ${label} date within ${maxDays} days from today.`
        : `Please choose a ${label} date within the current month.`;
    }
    if (blackoutDatesFor(methodName).has(value)) return `That ${label} date is unavailable. Please choose another date.`;
    return '';
  }

  function hintFor(methodName) {
    const label = methodName === 'pickup' ? 'pickup' : 'delivery';
    if (!hasAvailableDate(methodName)) {
      const maxDays = Number(DATE_CONFIG[methodName]?.maxDays);
      return Number.isFinite(maxDays) && maxDays >= 0
        ? `No ${label} dates are available within the next ${maxDays} days.`
        : `No ${label} dates are available for the rest of this month.`;
    }
    const leadDays = Math.max(0, Number(DATE_CONFIG[methodName]?.leadDays) || 0);
    const leadText = leadDays === 1
      ? `Same-day ${label} is not available. Earliest ${label} is tomorrow.`
      : leadDays > 1
        ? `Please allow at least ${leadDays} days for ${label}.`
        : `Same-day ${label} is available.`;
    const maxDays = Number(DATE_CONFIG[methodName]?.maxDays);
    if (Number.isFinite(maxDays) && maxDays >= 0) {
      return `${leadText} Dates can be selected up to ${maxDays} days from today.`;
    }
    return DATE_CONFIG[methodName]?.limitToCurrentMonth
      ? `${leadText} Dates can be selected through the end of this month.`
      : leadText;
  }

  function regularOnly(currentCart) {
    return currentCart?.item_count > 0 && (currentCart.items || []).every((item) => !itemIsMcGill(item));
  }

  function attrs(currentCart) {
    return currentCart?.attributes || {};
  }

  function storedType(currentCart) {
    const a = attrs(currentCart);
    if (a['Order Type'] === 'Delivery') return 'delivery';
    if (a['Order Type'] === 'Pickup') return 'pickup';
    if (a['Delivery-Method'] === 'localDelivery') return 'delivery';
    if (a['Delivery-Method'] === 'storePickup') return 'pickup';
    return '';
  }

  function method(currentCart) {
    return storedType(currentCart) || 'delivery';
  }

  function dateFor(currentCart, methodName) {
    const a = attrs(currentCart);
    return methodName === 'pickup'
      ? (a['Pickup Date'] || a['Delivery-Date'] || '')
      : (a['Delivery Date'] || a['Delivery-Date'] || '');
  }

  function timeFor(currentCart) {
    const a = attrs(currentCart);
    return a['Pickup Time'] || a['Delivery-Time'] || '';
  }

  function commentFor(currentCart, methodName) {
    const a = attrs(currentCart);
    return methodName === 'pickup'
      ? (a['Pickup Note'] || a['Delivery-Comment'] || '')
      : (a['Delivery Instructions'] || a['Delivery-Comment'] || '');
  }

  function valid(currentCart) {
    if (!regularOnly(currentCart)) return true;
    const currentMethod = storedType(currentCart);
    if (!currentMethod) return false;
    const selectedDate = dateFor(currentCart, currentMethod);
    if (!dateAllowed(selectedDate, currentMethod)) return false;
    if (currentMethod === 'pickup' && !timeFor(currentCart)) return false;
    return true;
  }

  function message(currentCart) {
    const currentMethod = storedType(currentCart);
    if (!currentMethod) return 'Please choose Delivery or Pickup and select an available date before checkout.';

    const issue = dateIssue(dateFor(currentCart, currentMethod), currentMethod);
    if (issue) return issue;
    if (currentMethod === 'pickup' && !timeFor(currentCart)) return 'Please choose a pickup time.';
    return '';
  }

  function cartContext(element) {
    return location.pathname.replace(/\/$/, '').endsWith('/cart') ||
      !!element?.closest('.mini-cart,cart-drawer,[id="MiniCart"],[id*="mini-cart" i],form#cart');
  }

  function styles() {
    if (document.getElementById('mmp-dd-style')) return;
    const style = document.createElement('style');
    style.id = 'mmp-dd-style';
    style.textContent = '.mmp-dd{margin:0 0 18px;padding:16px;border:1px solid #d7d7d7;border-radius:8px;background:#fff;text-align:left}.mmp-dd h3{margin:0 0 12px;font-size:16px}.mmp-dd-tabs{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px}.mmp-dd-tabs button{padding:10px;border:1px solid #aaa;border-radius:6px;background:#fff;font:inherit;cursor:pointer}.mmp-dd-tabs button[aria-pressed="true"]{background:#111;color:#fff;border-color:#111}.mmp-dd-panel{display:grid;gap:10px}.mmp-dd-panel[hidden]{display:none!important}.mmp-dd label{display:grid;gap:5px;font-size:14px;font-weight:600}.mmp-dd input,.mmp-dd select,.mmp-dd textarea{box-sizing:border-box;width:100%;padding:9px 10px;min-height:42px;border:1px solid #bbb;border-radius:5px;background:#fff;font:inherit}.mmp-dd-hint{margin:-3px 0 0;font-size:12px;opacity:.72}.mmp-dd-error{min-height:18px;margin-top:9px;color:#b42318;font-size:13px}.mmp-dd.saving{opacity:.65;pointer-events:none}';
    document.head.appendChild(style);
  }

  function html(key) {
    const element = document.createElement('div');
    element.className = 'mmp-dd';
    element.dataset.mmpDd = key;
    element.innerHTML = `<h3>How would you like to receive your order?</h3><div class="mmp-dd-tabs"><button type="button" data-method="delivery">Delivery</button><button type="button" data-method="pickup">Pickup</button></div><div class="mmp-dd-panel" data-panel="delivery"><label>Delivery date<input type="date" data-delivery-date></label><p class="mmp-dd-hint" data-delivery-hint></p><label>Delivery instructions (optional)<textarea rows="2" data-delivery-comment placeholder="Apartment, buzzer, concierge, or other delivery instructions"></textarea></label></div><div class="mmp-dd-panel" data-panel="pickup" hidden><label>Pickup date<input type="date" data-pickup-date></label><p class="mmp-dd-hint" data-pickup-hint></p><label>Pickup time<select data-pickup-time><option value="">Select time</option>${SLOTS.map(([value, label]) => `<option value="${value}">${label}</option>`).join('')}</select></label><label>Pickup note (optional)<textarea rows="2" data-pickup-comment placeholder="Anything we should know about your pickup?"></textarea></label></div><div class="mmp-dd-error" role="alert"></div>`;
    return element;
  }

  function targets() {
    const out = [];
    document.querySelectorAll('.cart__ctas').forEach((element) => {
      if (cartContext(element) && element.parentElement) out.push(['page', element.parentElement, element]);
    });
    document.querySelectorAll('.mini-cart .button-container,cart-drawer .button-container').forEach((element) => {
      if (element.parentElement) out.push(['drawer', element.parentElement, element]);
    });
    return out;
  }

  function sync(element, currentCart) {
    const currentMethod = method(currentCart);
    const deliveryRules = rulesFor('delivery');
    const pickupRules = rulesFor('pickup');
    const deliveryAvailable = hasAvailableDate('delivery');
    const pickupAvailable = hasAvailableDate('pickup');

    element.querySelectorAll('[data-method]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.method === currentMethod ? 'true' : 'false');
    });
    element.querySelector('[data-panel="delivery"]').hidden = currentMethod !== 'delivery';
    element.querySelector('[data-panel="pickup"]').hidden = currentMethod !== 'pickup';

    const deliveryDate = element.querySelector('[data-delivery-date]');
    const deliveryComment = element.querySelector('[data-delivery-comment]');
    const pickupDate = element.querySelector('[data-pickup-date]');
    const pickupTime = element.querySelector('[data-pickup-time]');
    const pickupComment = element.querySelector('[data-pickup-comment]');

    deliveryDate.min = deliveryRules.min;
    deliveryDate.max = deliveryRules.max;
    deliveryDate.disabled = !deliveryAvailable;
    pickupDate.min = pickupRules.min;
    pickupDate.max = pickupRules.max;
    pickupDate.disabled = !pickupAvailable;
    pickupTime.disabled = !pickupAvailable;

    const storedDeliveryDate = dateFor(currentCart, 'delivery');
    const storedPickupDate = dateFor(currentCart, 'pickup');
    deliveryDate.value = currentMethod === 'delivery' && dateAllowed(storedDeliveryDate, 'delivery') ? storedDeliveryDate : '';
    deliveryComment.value = currentMethod === 'delivery' ? commentFor(currentCart, 'delivery') : '';
    pickupDate.value = currentMethod === 'pickup' && dateAllowed(storedPickupDate, 'pickup') ? storedPickupDate : '';
    pickupTime.value = currentMethod === 'pickup' ? timeFor(currentCart) : '';
    pickupComment.value = currentMethod === 'pickup' ? commentFor(currentCart, 'pickup') : '';

    element.querySelector('[data-delivery-hint]').textContent = hintFor('delivery');
    element.querySelector('[data-pickup-hint]').textContent = hintFor('pickup');
    element.querySelector('.mmp-dd-error').textContent = validationError || message(currentCart);
    element.classList.toggle('saving', saving);
  }

  function setCheckout(currentCart) {
    const block = regularOnly(currentCart) && !valid(currentCart);
    document.querySelectorAll('button[name="checkout"],input[name="checkout"]').forEach((button) => {
      if (!cartContext(button)) return;
      if (block) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.mmpDdDisabled = '1';
      } else if (button.dataset.mmpDdDisabled) {
        if (button.dataset.mcgillGuardDisabled !== 'true') {
          button.disabled = currentCart.item_count === 0;
          button.removeAttribute('aria-disabled');
        }
        delete button.dataset.mmpDdDisabled;
      }
    });

    document.querySelectorAll('.cart__dynamic-checkout-buttons,.additional-checkout-buttons').forEach((container) => {
      if (!cartContext(container)) return;
      if (block) {
        if (!container.dataset.mmpDdHidden) container.dataset.mmpDdDisplay = container.style.display || '';
        container.style.display = 'none';
        container.dataset.mmpDdHidden = '1';
      } else if (container.dataset.mmpDdHidden) {
        if (container.dataset.mcgillGuardHidden !== 'true') container.style.display = container.dataset.mmpDdDisplay || '';
        delete container.dataset.mmpDdHidden;
        delete container.dataset.mmpDdDisplay;
      }
    });
  }

  function render(currentCart) {
    cart = currentCart;
    if (!regularOnly(currentCart)) {
      validationError = '';
      document.querySelectorAll('[data-mmp-dd]').forEach((element) => element.remove());
      setCheckout(currentCart);
      return;
    }

    styles();
    targets().forEach(([key, parent, before]) => {
      let element = parent.querySelector(`[data-mmp-dd="${key}"]`);
      if (!element) {
        element = html(key);
        parent.insertBefore(element, before);
      }
      sync(element, currentCart);
    });
    setCheckout(currentCart);
  }

  async function read() {
    const response = await fetchCart('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }

  function safeValues(methodName, values = {}) {
    return {
      ...values,
      date: values.date && dateAllowed(values.date, methodName) ? values.date : ''
    };
  }

  async function save(methodName, rawValues = {}) {
    if (saving) return;
    const values = safeValues(methodName, rawValues);
    saving = true;
    document.querySelectorAll('[data-mmp-dd]').forEach((element) => element.classList.add('saving'));

    const next = methodName === 'pickup' ? {
      'Order Type': 'Pickup',
      'Delivery Date': '',
      'Delivery Instructions': '',
      'Pickup Date': values.date || '',
      'Pickup Time': values.time || '',
      'Pickup Note': values.comment || ''
    } : {
      'Order Type': 'Delivery',
      'Delivery Date': values.date || '',
      'Delivery Instructions': values.comment || '',
      'Pickup Date': '',
      'Pickup Time': '',
      'Pickup Note': ''
    };

    Object.assign(next, {
      'Delivery-Method': '',
      'Delivery-Location': '',
      'Profile-Id': '',
      'Delivery-Date': '',
      'Delivery-Time': '',
      'Delivery-Comment': '',
      'Order-Date': ''
    });

    try {
      const response = await fetchCart('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { ...attrs(cart), ...next } })
      });
      if (!response.ok) throw new Error('Unable to save selection');
      render(await response.json());
    } catch (_) {
      validationError = 'We could not save your selection. Please try again.';
      document.querySelectorAll('.mmp-dd-error').forEach((element) => {
        element.textContent = validationError;
      });
    } finally {
      saving = false;
      if (cart) render(cart);
    }
  }

  function values(element, methodName) {
    return methodName === 'pickup' ? {
      date: element.querySelector('[data-pickup-date]').value,
      time: element.querySelector('[data-pickup-time]').value,
      comment: element.querySelector('[data-pickup-comment]').value.trim()
    } : {
      date: element.querySelector('[data-delivery-date]').value,
      comment: element.querySelector('[data-delivery-comment]').value.trim()
    };
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-method]');
    if (button) {
      const element = button.closest('[data-mmp-dd]');
      if (element) {
        event.preventDefault();
        validationError = '';
        save(button.dataset.method, values(element, button.dataset.method));
        return;
      }
    }

    const checkout = event.target.closest?.('button[name="checkout"],input[name="checkout"]');
    if (checkout && cartContext(checkout) && cart && regularOnly(cart) && !valid(cart)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelectorAll('.mmp-dd-error').forEach((element) => {
        element.textContent = validationError || message(cart);
      });
      document.querySelector('[data-mmp-dd]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, true);

  document.addEventListener('change', (event) => {
    const element = event.target.closest?.('[data-mmp-dd]');
    if (!element) return;
    const currentMethod = element.querySelector('[data-method][aria-pressed="true"]')?.dataset.method || 'delivery';
    if (!event.target.matches('[data-delivery-date],[data-delivery-comment],[data-pickup-date],[data-pickup-time],[data-pickup-comment]')) return;

    if (event.target.matches('[data-delivery-date],[data-pickup-date]')) {
      if (event.target.value && !dateAllowed(event.target.value, currentMethod)) {
        validationError = dateIssue(event.target.value, currentMethod);
        event.target.value = '';
        element.querySelector('.mmp-dd-error').textContent = validationError;
      } else {
        validationError = '';
      }
    }
    save(currentMethod, values(element, currentMethod));
  });

  document.addEventListener('submit', (event) => {
    if (!cart || !regularOnly(cart) || valid(cart)) return;
    const form = event.target;
    if (form instanceof HTMLFormElement && form.querySelector('[name="checkout"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelectorAll('.mmp-dd-error').forEach((element) => {
        element.textContent = validationError || message(cart);
      });
    }
  }, true);

  async function refresh() {
    if (saving) return;
    try {
      render(await read());
    } catch (_) {
      // Leave the storefront usable if cart data cannot be read temporarily.
    }
  }

  function later() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 220);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1 && !node.closest?.('[data-mmp-dd]')))) later();
  });
  const start = () => document.body && observer.observe(document.body, { childList: true, subtree: true });
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
